import React from "react";
import { Mic, MicOff, Video, VideoOff, ScreenShare, ScreenShareOff, Hand, MessageSquare, PhoneOff, Circle, Users2, Sparkles } from "lucide-react";

/** Controls bar for LiveKit-powered Cloudivo Meet (Enterprise mode). Adds
 * cloud recording (server-side egress) and breakout rooms on top of the
 * base call controls shared with the legacy mesh mode. */
export function LiveKitMeetingControls({
  micOn, camOn, screenSharing, canUseCamera, handRaised, chatOpen, isHost,
  isRecording, onToggleRecording, onOpenBreakouts, onOpenBackground,
  onToggleMic, onToggleCam, onToggleScreenShare, onToggleHand, onToggleChat, onLeave,
}) {
  const btnClass = (active) =>
    `w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
      active ? "bg-white/15 text-white" : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"
    }`;

  return (
    <div className="flex items-center justify-center gap-3 py-4 flex-wrap" data-testid="livekit-meeting-controls-bar">
      <button onClick={onToggleMic} data-testid="livekit-toggle-mic-btn" className={btnClass(micOn)} title={micOn ? "Mute" : "Unmute"}>
        {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5 text-red-400" />}
      </button>
      {canUseCamera && (
        <button onClick={onToggleCam} data-testid="livekit-toggle-cam-btn" className={btnClass(camOn)} title={camOn ? "Stop Video" : "Start Video"}>
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5 text-red-400" />}
        </button>
      )}
      {canUseCamera && (
        <button onClick={onToggleScreenShare} data-testid="livekit-toggle-screenshare-btn" className={btnClass(screenSharing)} title="Share Screen">
          {screenSharing ? <ScreenShareOff className="w-5 h-5 text-cyan-400" /> : <ScreenShare className="w-5 h-5" />}
        </button>
      )}
      {canUseCamera && (
        <button onClick={onOpenBackground} data-testid="livekit-background-btn" className={btnClass(false)} title="Virtual Background">
          <Sparkles className="w-5 h-5" />
        </button>
      )}
      {!isHost && (
        <button onClick={onToggleHand} data-testid="livekit-raise-hand-btn" className={btnClass(handRaised)} title="Raise Hand">
          <Hand className={`w-5 h-5 ${handRaised ? "text-amber-400" : ""}`} />
        </button>
      )}
      {/* Breakout rooms are visible to everyone — backend allows any participant to view/join
          a breakout room the host created (only creating/closing rooms is host-only, and that's
          already gated inside BreakoutRoomsPanel itself). */}
      <button onClick={onOpenBreakouts} data-testid="livekit-breakout-rooms-btn" className={btnClass(false)} title="Breakout Rooms">
        <Users2 className="w-5 h-5" />
      </button>
      {isHost && (
        <button onClick={onToggleRecording} data-testid="livekit-toggle-recording-btn" className={btnClass(isRecording)} title={isRecording ? "Stop Cloud Recording" : "Start Cloud Recording"}>
          <Circle className={`w-5 h-5 ${isRecording ? "text-red-500 fill-red-500 animate-pulse" : ""}`} />
        </button>
      )}
      <button onClick={onToggleChat} data-testid="livekit-toggle-chat-btn" className={btnClass(chatOpen)} title="Chat">
        <MessageSquare className="w-5 h-5" />
      </button>
      <button onClick={onLeave} data-testid="livekit-leave-btn" className="w-11 h-11 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors" title={isHost ? "End Meeting" : "Leave"}>
        <PhoneOff className="w-5 h-5" />
      </button>
    </div>
  );
}
